// docs/71 — the hero inventory: a paper doll of ten worn slots around the hero, the hero's numbers
// (the sim's own `derive_stats` over what is worn, with the change any item would make), a pack
// of 20 cells (more with a bigger pack), and a details card with what the selected item does and
// what can be done with it: wear it, take it off, sell, trade or throw away. Items drag from the
// pack onto a slot, from slot to slot, back to the pack, onto the discard pile, or onto another of
// my heroes' tabs to hand it over. `openInventory(heroId)` from anywhere; `InventoryHost` is
// mounted once in App. The module (fk-world `items.rs`) is the judge of every move; `gear.ts`
// only predicts it so the doll can light the slots that fit and say why one does not.
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, Counter, EmptyState, HeroSprite, Modal, OryxIcon, Segmented, confirmDialog } from '../components';
import { usePuzzleRoom, useWorldHeroes } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { identityHex } from '../../core/net/connection';
import { rpc, useToasts } from '../../core/net/reducers';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { itemDetails } from '../../core/sim/items';
import { autoSlot, filterGroup, gradeWord, sellValue, planUnwear, planWear, PACKS, SLOT, SLOTS, sortPack, statDiff, STAT_ROWS, statValue, upgradeSegs, wornAfter, wornAfterUps, classAllowed, slotLabel, type PackFilter, type PackSort, type StatKey, isPackSort } from '../../core/sim/gear';
import { campUse, campUseOffered } from '../../core/items/catalogue';
import { BOONS, campVerb } from '../../core/items/describe';
import { useHeroBoons, useItemInCamp } from '../../core/items/live';
import { playItemSfx } from '../../core/items/sound';
import { ItemArtIcon, ItemBody, UpgradeAura, UpgradeList } from './ItemCard';
import { dyedLine } from '../../core/items/dyes';
import { upgradedName } from '../../core/items/upgrades';
import { UpgradeGifts } from './UpgradeGifts';
import { playSfx } from '../../core/audio';
import { serverNow } from '../../core/time';
import { loreOf } from './lore';
import { dropAt, useDrag, useInventory, type DropTarget } from './store';
import { useFightGear, type GearMoveView } from './fightGear';
import { useHeroGear, type GearItem, type HeroGear } from './useHeroGear';
import { TradeComposer, Trades, TradeWatcher, useIncomingTrades } from './Trades';
import { useOutfitting, type Outfitting } from './outfitting';
import './inventory.css';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** `sell_item` pays a quarter of `price(tier)`: 20 / 60 / 180 → 5 / 15 / 45 gold; tier 4 has no price. */
export const sellPrice = (tier: number): number => [0, 5, 15, 45][tier] ?? 0;
/** docs/72: a catalogue piece sells for a quarter of its price (the rarity's default when it names
 * none); treasure for its full price (AUTHORING.md rule 1). */
export const sellPriceOf = (it: Pick<GearItem, 'tier' | 'defId'> & { upgradeSpent?: number }): number => sellValue(it.defId, it.tier) + Math.floor((it.upgradeSpent ?? 0) / 4);

/** docs/34: what putting a piece on or off sounds like, by where it goes. */
function gearSound(it: GearItem): 'ui.equip_weapon' | 'ui.equip_armour' | 'ui.equip_trinket' {
  const s = it.def.slots[0];
  if (s === SLOT.MainHand) return 'ui.equip_weapon';
  if (s === SLOT.Trinket || s === SLOT.Trinket2 || s === SLOT.Neck || s === SLOT.Waist || s === undefined) return 'ui.equip_trinket';
  return 'ui.equip_armour';
}
/** docs/72 IT19: a catalogue piece puts on with its own `sfx.equip` at its own variation. */
function playGear(it: GearItem): void { if (!playItemSfx(it.cat, 'equip')) playSfx(gearSound(it)); }

/* ---------- "new" markers: pieces that arrived since the player last looked ---------- */
const SEEN_KEY = 'fk.inv.seen';
const SORT_KEY = 'fk.inv.sort';
const NEW_FOR_MS = 3 * 24 * 3600 * 1000;
function readSeen(): Set<string> | undefined {
  try { const v = localStorage.getItem(SEEN_KEY); return v ? new Set(JSON.parse(v) as string[]) : undefined; } catch { return undefined; }
}
function writeSeen(s: Set<string>) { try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-600))); } catch { /* private mode */ } }
function useSeen(items: readonly GearItem[]) {
  const [seen, setSeen] = useState<Set<string>>(() => readSeen() ?? new Set());
  // the first time anyone opens the inventory, nothing already carried is "new"
  useEffect(() => {
    if (readSeen() || !items.length) return;
    const s = new Set(items.map((i) => i.id.toString()));
    writeSeen(s); setSeen(s);
  }, [items]);
  const isNew = (i: GearItem) => !seen.has(i.id.toString()) && serverNow() - i.acquiredMs < NEW_FOR_MS;
  const mark = (i: GearItem) => { if (seen.has(i.id.toString())) return; const s = new Set(seen); s.add(i.id.toString()); writeSeen(s); setSeen(s); };
  return { isNew, mark };
}

/* ---------- drag ---------- */

/** Pointer drag for one tile (mouse, pen and touch): a press that moves 5 px becomes a drag with a ghost; one that doesn't is a click. */
function useItemPointer(it: GearItem, enabled: boolean, onClick: () => void, onDrop: (t: DropTarget | undefined) => void) {
  return (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const x0 = e.clientX, y0 = e.clientY;
    let dragging = false;
    const move = (ev: PointerEvent) => {
      if (!enabled) return;
      if (!dragging && Math.hypot(ev.clientX - x0, ev.clientY - y0) > 5) { dragging = true; useDrag.getState().start(it.id, it.defId, it.name, ev.clientX, ev.clientY); playSfx('ui.item'); }
      if (dragging) { ev.preventDefault(); useDrag.getState().move(ev.clientX, ev.clientY, dropAt(ev.clientX, ev.clientY)); }
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (dragging) { const t = dropAt(ev.clientX, ev.clientY); useDrag.getState().end(); onDrop(t); }
      else if (ev.type === 'pointerup') onClick();
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
}

function DragGhost() {
  const def = useDrag((s) => s.def);
  const name = useDrag((s) => s.name);
  const x = useDrag((s) => s.x), y = useDrag((s) => s.y);
  const over = useDrag((s) => s.over);
  if (def === undefined) return null;
  return createPortal(
    <div className={`fk-inv__ghost ${over ? 'fk-inv__ghost--over' : ''}`} style={{ left: x, top: y }} aria-hidden="true" data-testid="inventory-drag-ghost"><ItemArtIcon def={def} name={name} scale={3} /></div>,
    document.body,
  );
}

/* ---------- tiles ---------- */

function ItemTile({ it, selected, isNew, locked, planned, onSelect, onDrop, onQuick, onHover, size = 'md' }: {
  it: GearItem; selected: boolean; isNew?: boolean; locked: boolean; size?: 'md' | 'lg';
  /** docs/83 PO8: in a fight, this piece's place is planned (it moves when the turn plays). */
  planned?: boolean;
  onSelect(): void; onDrop(t: DropTarget | undefined): void; onQuick(): void; onHover(on: boolean): void;
}) {
  const dragged = useDrag((s) => s.item === it.id);
  const down = useItemPointer(it, !locked, onSelect, onDrop);
  return (
    <button type="button" className={`fk-inv__tile fk-inv__tile--${it.rarity} fk-inv__tile--${size} ${selected ? 'is-selected' : ''} ${dragged ? 'is-dragged' : ''} ${it.offered ? 'is-offered' : ''} ${planned ? 'is-planned' : ''}`}
      onPointerDown={down} onDoubleClick={onQuick} onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)} onFocus={() => onHover(true)} onBlur={() => onHover(false)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      aria-pressed={selected} aria-label={`${upgradedName(it.name, it.upgrades)}, ${gradeWord(it.defId, it.tier, it.relic).toLowerCase()}${it.slot !== null ? `, worn on the ${slotLabel(it.slot).toLowerCase()}` : ''}`}
      title={`${upgradedName(it.name, it.upgrades)} · ${gradeWord(it.defId, it.tier, it.relic)}${it.slot === null && it.def.slots.length ? ' · double-click to wear' : it.slot !== null ? ' · double-click to take off' : ''}`}
      data-testid="inv-item" data-item-id={it.id.toString()} data-item-name={it.name} data-slot={it.slot ?? 'pack'} data-rarity={it.rarity} data-planned={planned || undefined} data-upgrades={it.upgrades.length || undefined}>
      <ItemArtIcon def={it.defId} name={it.name} scale={size === 'lg' ? 3 : 2} dye={it.dye} />
      <UpgradeAura upgrades={it.upgrades} />
      {it.tier > 1 && !it.relic && !it.cat && <span className="fk-inv__pips" aria-hidden="true">{'◆'.repeat(Math.min(3, it.tier - 1))}</span>}
      {isNew && <span className="fk-inv__new" aria-label="new">new</span>}
      {it.offered && <span className="fk-inv__offered" title="In a trade offer" aria-label="offered in a trade">⇄</span>}
      {planned && <span className="fk-inv__planned" title="Planned: moves here when the turn plays" aria-label="planned">planned</span>}
    </button>
  );
}

/* ---------- the paper doll ---------- */

// docs/72 IT2: Waist under the Back, Legs between the Hands and the Feet
const DOLL_LEFT = [SLOT.Head, SLOT.Neck, SLOT.Cape, SLOT.Waist, SLOT.Trinket];
const DOLL_RIGHT = [SLOT.Torso, SLOT.Hands, SLOT.Legs, SLOT.Feet, SLOT.Trinket2];

function SlotBox({ slot, children, state, hint }: { slot: number; children?: ReactNode; state?: 'fits' | 'nope' | 'over' | 'land'; hint?: string }) {
  const info = SLOTS[slot]!;
  return (
    <div className={`fk-inv__slot ${state ? `fk-inv__slot--${state}` : ''} ${children ? 'is-full' : 'is-empty'}`} data-drop={`slot:${slot}`} data-testid="inv-slot" data-slot={slot} data-slot-key={info.key} title={hint ?? `${info.label}: ${info.hint}`}>
      {children ?? <span className="fk-inv__slot-ghost" aria-hidden="true"><OryxIcon name={info.icon} scale={2} /></span>}
      <span className="fk-inv__slot-label">{info.label}{slot === SLOT.Trinket ? ' I' : slot === SLOT.Trinket2 ? ' II' : ''}</span>
    </div>
  );
}

/* ---------- the inventory ---------- */

export function InventoryHost() {
  const heroId = useInventory((s) => s.heroId);
  const close = useInventory((s) => s.close);
  const tab = useInventory((s) => s.tab);
  const setTab = useInventory((s) => s.setTab);
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const heroes = useWorldHeroes(worldId);
  const hero = heroes.find((h) => h.id === heroId);
  const incoming = useIncomingTrades(worldId);
  return (
    <>
      <TradeWatcher worldId={worldId} />
      <DragGhost />
      <Modal open={!!hero} onClose={close} eyebrow="Inventory" title={hero?.name} size="full" testId="inventory" className="fk-inv">
        {hero && worldId !== undefined && (
          <>
            <HeroTabs worldId={worldId} heroId={hero.id} me={me} />
            <div className="fk-inv__tabs" role="tablist" aria-label="Inventory views">
              <button type="button" role="tab" aria-selected={tab === 'gear'} className="fk-inv__tab" onClick={() => setTab('gear')} data-testid="inv-tab-gear">Gear</button>
              <button type="button" role="tab" aria-selected={tab === 'trades'} className="fk-inv__tab" onClick={() => setTab('trades')} data-testid="inv-tab-trades">Trades{incoming.length > 0 && <Badge tone="amber" filled pulse>{incoming.length}</Badge>}</button>
            </div>
            {tab === 'gear' ? <GearView worldId={worldId} heroId={hero.id} /> : <Trades worldId={worldId} heroId={hero.id} />}
          </>
        )}
      </Modal>
    </>
  );
}

/** My heroes in this World as tabs; drop an item on one to hand it over. */
function HeroTabs({ worldId, heroId, me }: { worldId: bigint; heroId: bigint; me: string }) {
  const heroes = useWorldHeroes(worldId).filter((h) => identityHex(h.owner) === me && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  const open = useInventory((s) => s.open);
  const over = useDrag((s) => s.over);
  const dragging = useDrag((s) => s.item !== undefined);
  if (heroes.length < 2) return null;
  return (
    <nav className="fk-inv__heroes" aria-label="Your heroes">
      {heroes.map((h) => {
        const cls = CLASS_NAMES[h.class] ?? 'fighter';
        const target = dragging && h.id !== heroId;
        const hot = target && over?.kind === 'hero' && over.heroId === h.id;
        return (
          <button key={h.id.toString()} type="button" className={`fk-inv__hero ${h.id === heroId ? 'is-current' : ''} ${target ? 'is-target' : ''} ${hot ? 'is-over' : ''}`}
            onClick={() => open(h.id)} aria-current={h.id === heroId ? 'page' : undefined} data-drop={h.id === heroId ? undefined : `hero:${h.id}`} data-testid="inv-hero-tab" data-hero-name={h.name}
            title={h.id === heroId ? h.name : `${h.name} — drop an item here to hand it over`}>
            <HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={cls} scale={2} />
            <span className="fk-inv__hero-name">{h.name}</span>
            <span className="fk-inv__hero-lv">L{h.level}</span>
            {target && <span className="fk-inv__hero-give" aria-hidden="true">give</span>}
          </button>
        );
      })}
    </nav>
  );
}

function GearView({ worldId, heroId }: { worldId: bigint; heroId: bigint }) {
  const g0 = useHeroGear(worldId, heroId);
  // docs/146: while my company outfits nothing is sold and the starting kit stays in the company
  const outfit = useOutfitting(worldId);
  const heroes = useWorldHeroes(worldId);
  // docs/83 PO8: in a fight the paper doll shows the gear as planned for this turn's Change Gear
  const fight = useFightGear(g0, heroId);
  const g = fight.view;
  const h = g.hero!;
  const selectedId = useInventory((s) => s.selected);
  const select = useInventory((s) => s.select);
  const push = useToasts((s) => s.push);
  const dragItem = useDrag((s) => s.item);
  const over = useDrag((s) => s.over);
  const [hover, setHover] = useState<bigint>();
  const [busy, setBusy] = useState(false);
  const [landed, setLanded] = useState<number>();
  const [filter, setFilter] = useState<PackFilter>('all');
  const [sort, setSort] = useState<PackSort>(() => { try { const v = localStorage.getItem(SORT_KEY); return isPackSort(v) ? v : 'kind'; } catch { return 'kind'; } });
  const [trading, setTrading] = useState<bigint[] | undefined>();
  const { isNew, mark } = useSeen(g.items);
  const deployed = h.status === 1;
  const planning = deployed && fight.canPlan;
  const locked = (deployed && !planning) || busy;
  // docs/72 IT10: a hint piece is the one camp use spent in the field, in a puzzle room still open
  const room = usePuzzleRoom(deployed ? h.activeEncounter : undefined);
  const inPuzzle = !!room && !room.forced;
  const classId = CLASS_NAMES[h.class] ?? 'fighter';
  const selected = g.items.find((i) => i.id === selectedId);
  const dragged = g.items.find((i) => i.id === dragItem);
  useEffect(() => { try { localStorage.setItem(SORT_KEY, sort); } catch { /* private mode */ } }, [sort]);
  useEffect(() => { if (selectedId !== undefined && !selected) select(undefined); }, [selectedId, selected, select]);

  const run = async (fn: () => Promise<{ ok: boolean }>, ok?: () => void) => {
    setBusy(true);
    const r = await fn();
    setBusy(false);
    if (r.ok) ok?.();
    return r.ok;
  };
  const opts = { cls: h.class, capacity: g.capacity };
  const wearIn = async (it: GearItem, slot: number) => {
    const plan = planWear(it, slot, g.items, opts);
    if (!plan.ok) { push(cap(plan.reason) + '.', 'error'); playSfx('ui.error'); return; }
    if (plan.noop) return;
    if (deployed) { await run(async () => ({ ok: await fight.plan(it.id, slot) }), () => { playGear(it); setLanded(slot); setTimeout(() => setLanded(undefined), 700); }); return; }
    await run(() => rpc.equipTo(it.id, slot), () => { playGear(it); setLanded(slot); setTimeout(() => setLanded(undefined), 700); });
  };
  const wear = (it: GearItem) => {
    const s = autoSlot(it.def, g.items.filter((i) => i.slot !== null));
    if (s !== undefined) void wearIn(it, s);
    // a dagger beside a two-handed weapon: no free hand, and it never takes the main hand (`items::equip`)
    else push(it.def.slots.length ? `${h.name} has no free hand: take the two-handed weapon off first.` : `${it.name} is not worn.`, 'error');
  };
  const unwear = async (it: GearItem) => {
    const p = planUnwear(it, g.items, g.capacity);
    if (!p.ok) { push(cap(p.reason) + '.', 'error'); playSfx('ui.error'); return; }
    if (deployed) { await run(async () => ({ ok: await fight.plan(it.id, null) }), () => playSfx(gearSound(it))); return; }
    await run(() => rpc.unequip(it.id), () => playSfx(gearSound(it)));
  };
  // docs/72 IT17: a pack piece used in camp (its boon, experience, gold, a card or a hint)
  const useInCamp = async (it: GearItem) => {
    const c = campUse(it.cat);
    if (!c || !it.cat) return;
    await run(() => useItemInCamp(it.id), () => { if (!playItemSfx(it.cat, 'use')) playSfx('ui.confirm'); push(`${h.name} ${campVerb(it.cat!).toLowerCase() === 'use' ? 'uses' : `${campVerb(it.cat!).toLowerCase()}s`} the ${it.name}.`, 'success'); select(undefined); });
  };
  const discard = async (it: GearItem) => {
    if (it.relic) { push('A relic is not thrown away.', 'error'); return; }
    if (it.slot === SLOT.Torso) { push('Swap the armour before throwing it away.', 'error'); return; }
    const ok = await confirmDialog({ eyebrow: 'Throw it away?', title: `Discard ${it.name}?`, text: <>It is gone for good — nobody can get it back.{sellPriceOf(it) > 0 ? <> A shop would pay <b>{sellPriceOf(it)} gold</b> for it.</> : null}</>, confirmLabel: 'Discard', danger: true });
    if (!ok) return;
    await run(() => rpc.discardItem(it.id), () => { playSfx('ui.back'); push(`${h.name} throws away the ${it.name}.`, 'info'); select(undefined); });
  };
  const sell = async (it: GearItem) => {
    const price = sellPriceOf(it);
    const ok = await confirmDialog({ title: `Sell ${it.name}?`, text: it.cat?.family === 'treasure' ? `Treasure sells for its full worth: ${price} gold.` : `The shop pays ${price} gold for it — a quarter of what it charges.`, confirmLabel: `Sell for ${price}g` });
    if (!ok) return;
    await run(() => rpc.sellItem(it.id), () => { playSfx(price >= 45 ? 'ui.coins_big' : 'ui.trade'); push(`Sold ${it.name} for ${price} gold.`, 'success'); select(undefined); });
  };
  const give = async (it: GearItem, to: bigint) => {
    if (to === h.id) return;
    // docs/146 OG2: starting kit stays on its hero while my company outfits (Edit remakes it)
    if (outfit.kit.has(it.id)) { push(`The ${it.name} is ${h.name}'s starting kit: it stays with them ${outfit.gathering ? 'until the World begins' : 'until you set out'}.`, 'error'); playSfx('ui.error'); return; }
    await run(() => rpc.offerTrade(h.id, to, [it.id], 0, [], 0, ''), () => { playSfx('ui.trade'); push(`${h.name} hands over the ${it.name}.`, 'success'); });
  };
  const onDrop = (it: GearItem) => (t: DropTarget | undefined) => {
    if (!t) return;
    // in a fight only what is worn changes: nothing is thrown away or handed over
    if (deployed && (t.kind === 'trash' || t.kind === 'hero')) { push('Not in the middle of a fight.', 'error'); return; }
    if (t.kind === 'slot') void wearIn(it, t.slot);
    else if (t.kind === 'pack') { if (it.slot !== null) void unwear(it); }
    else if (t.kind === 'trash') void discard(it);
    else if (t.kind === 'hero') void give(it, t.heroId);
  };
  const quick = (it: GearItem) => { if (locked) return; if (it.slot !== null) void unwear(it); else if (it.def.slots.length) wear(it); };
  const pick = (it: GearItem) => { mark(it); select(selectedId === it.id ? undefined : it.id); playSfx('ui.click'); };

  // the change the item under the pointer (or being dragged) would make to the numbers
  const probe = dragged ?? g.items.find((i) => i.id === hover) ?? selected;
  const probeSlot = probe ? (dragged && over?.kind === 'slot' ? over.slot : dragged && over?.kind === 'pack' ? null : probe.slot === null ? autoSlot(probe.def, g.items.filter((i) => i.slot !== null)) : null) : undefined;
  const preview = useMemo(() => {
    if (!probe || !g.stats) return undefined;
    if (probeSlot === null || probeSlot === undefined) {
      if (probe.slot === null) return undefined;
      // taking it off
      const rest = g.items.filter((i) => i.slot !== null && i.id !== probe.id);
      return { label: `without the ${probe.name}`, stats: g.previewWorn(rest.flatMap((i) => [i.slot!, i.defId, i.tier]), upgradeSegs(rest)) };
    }
    const plan = planWear(probe, probeSlot, g.items, opts);
    if (!plan.ok || plan.noop) return undefined;
    return { label: `wearing the ${probe.name}`, stats: g.previewWorn(wornAfter(g.items, probe, probeSlot, plan), wornAfterUps(g.items, probe, plan)), off: plan.toPack.map((x) => x.name) };
  }, [probe, probeSlot, g.items, g.stats, g.capacity]);

  // which slots light up for the dragged item
  const slotState = (slot: number): 'fits' | 'nope' | 'over' | 'land' | undefined => {
    if (landed === slot) return 'land';
    if (!dragged) return undefined;
    const fits = dragged.def.slots.includes(slot) && classAllowed(dragged.def.classMask, h.class);
    if (over?.kind === 'slot' && over.slot === slot) return fits ? 'over' : 'nope';
    return fits ? 'fits' : undefined;
  };
  const slotHint = (slot: number): string | undefined => {
    if (!dragged || !(over?.kind === 'slot' && over.slot === slot)) return undefined;
    const p = planWear(dragged, slot, g.items, opts);
    return p.ok ? undefined : cap(p.reason);
  };
  const tile = (it: GearItem, size: 'md' | 'lg' = 'md') => (
    <ItemTile key={it.id.toString()} it={it} size={size} selected={selectedId === it.id} isNew={isNew(it)} locked={locked} planned={fight.planned.has(it.id)}
      onSelect={() => pick(it)} onDrop={onDrop(it)} onQuick={() => quick(it)} onHover={(on) => { setHover(on ? it.id : undefined); if (on) mark(it); }} />
  );
  const slot = (s: number) => <SlotBox key={s} slot={s} state={slotState(s)} hint={slotHint(s)}>{g.worn.get(s) && tile(g.worn.get(s)!, 'lg')}</SlotBox>;

  const shown = sortPack(g.pack.filter((i) => filter === 'all' || filterGroup(i.def) === filter), sort);
  const next = PACKS[g.grade + 1];
  const empty = Math.max(0, g.capacity - g.pack.length);
  const full = g.pack.length >= g.capacity;
  const species = cap(SPECIES_NAMES[h.species] ?? '');
  const overPack = dragged && over?.kind === 'pack' && dragged.slot !== null;
  const overTrash = dragged && over?.kind === 'trash';

  return (
    <div className="fk-inv__gear" data-testid="inv-gear" data-hero-id={h.id.toString()} data-deployed={deployed || undefined}>
      {deployed && !planning && <div className="fk-inv__sealed" role="status" data-testid="inv-sealed"><OryxIcon name="item_147" scale={1} /> {fight.inFight && fight.why
        ? <>{h.name} can't change gear now: {fight.why}. With no foe in sight, gear changes as the hero's bonus action.</>
        : <>{h.name} is in the field: the pack is sealed until the fight is over. Use items from the encounter's bag.</>}</div>}
      {!deployed && <UpgradeGifts g={g} selected={selected} onPick={(id) => select(id)} />}
      {planning && <FightPlanNote name={h.name} moves={fight.moves} items={g0.items} onClear={() => void run(async () => { await fight.clear(); return { ok: true }; })} busy={busy} />}
      {/* the paper doll */}
      <section className="fk-inv__doll" aria-label="Worn">
        <div className="fk-inv__doll-col">{DOLL_LEFT.map(slot)}</div>
        <div className="fk-inv__doll-mid">
          <div className={`fk-inv__figure fk-inv__figure--${classId}`}>
            <span className="fk-inv__figure-glow" aria-hidden="true" />
            <span className="fk-inv__figure-sprite"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classId} scale={4} title={h.name} /></span>
            <span className="fk-inv__figure-plinth" aria-hidden="true" />
          </div>
          <div className="fk-inv__figure-id">L{h.level} {cap(classId)} · {species}</div>
          <HeroBoons worldId={worldId} heroId={h.id} />
          <div className="fk-inv__hands">{slot(SLOT.MainHand)}{slot(SLOT.OffHand)}</div>
          <div className="fk-inv__hands-note" aria-live="polite">{g.worn.get(SLOT.MainHand)?.def.twoHanded ? 'Two-handed: both hands are full' : g.worn.get(SLOT.OffHand)?.def.slots.includes(SLOT.MainHand) ? 'A blade in each hand' : ' '}</div>
        </div>
        <div className="fk-inv__doll-col">{DOLL_RIGHT.map(slot)}</div>
      </section>

      {/* numbers + details */}
      <section className="fk-inv__middle">
        <StatsPanel g={g} preview={preview} />
        <Details g={g} it={selected} locked={locked} busy={busy} deployed={deployed} planning={planning} inPuzzle={inPuzzle} outfit={outfit} kitOf={selected ? heroes.find((x) => x.id === outfit.kit.get(selected.id))?.name : undefined}
          onWear={wear} onWearIn={(it, s) => void wearIn(it, s)} onUnwear={(it) => void unwear(it)} onUse={(it) => void useInCamp(it)} onSell={(it) => void sell(it)} onDiscard={(it) => void discard(it)} onTrade={(it) => setTrading([it.id])} />
      </section>

      {/* the pack */}
      <section className={`fk-inv__pack ${overPack ? 'is-over' : ''}`} data-drop="pack" aria-label="Pack" data-testid="inv-pack" data-count={g.pack.length} data-capacity={g.capacity}>
        <header className="fk-inv__pack-head">
          <span className="fk-inv__pack-title"><OryxIcon name="item_147" scale={2} /> <span><b>{PACKS[g.grade]?.name}</b><span className={`fk-inv__pack-count ${full ? 'is-full' : ''}`} data-testid="inv-pack-count">{g.pack.length} / {g.capacity}</span></span></span>
          <span className="fk-inv__purse" title={`${h.name}'s purse`}><OryxIcon name="item_coin_bag" scale={1} /><Counter value={h.gold} /></span>
        </header>
        <div className="fk-inv__pack-meter" aria-hidden="true"><span style={{ width: `${Math.min(100, (100 * g.pack.length) / g.capacity)}%` }} className={full ? 'is-full' : g.pack.length / g.capacity > 0.8 ? 'is-high' : ''} /></div>
        <div className="fk-inv__pack-tools">
          <Segmented label="Show" value={filter} onChange={setFilter} className="fk-inv__filter"
            options={[{ value: 'all', label: 'All' }, { value: 'weapons', label: 'Arms', title: 'Weapons, shields and foci' }, { value: 'armour', label: 'Wear', title: 'Armour, helms, capes, gloves, legs and boots' }, { value: 'trinkets', label: 'Charms', title: 'Rings, charms, neckwear and belts' }, { value: 'use', label: 'Use', title: 'Potions, bombs, scrolls, food, tools and the rest of the pack' }]} />
          <select className="fk-input fk-input--sm fk-select fk-inv__sort" value={sort} onChange={(e) => setSort(e.target.value as PackSort)} aria-label="Sort the pack" data-testid="inv-sort">
            <option value="kind">By kind</option><option value="tier">Best first</option><option value="newest">Newest</option><option value="name">A–Z</option>
          </select>
        </div>
        <div className="fk-inv__grid" role="list" aria-label={`Pack, ${g.pack.length} of ${g.capacity}`}>
          {shown.map((i) => <div key={i.id.toString()} role="listitem" className="fk-inv__cell is-full">{tile(i)}</div>)}
          {filter === 'all' && Array.from({ length: empty }, (_, k) => <div key={`e${k}`} className="fk-inv__cell" aria-hidden="true" />)}
          {filter === 'all' && next && Array.from({ length: Math.min(10, next.capacity - g.capacity) }, (_, k) => <div key={`l${k}`} className="fk-inv__cell fk-inv__cell--locked" aria-hidden="true" title={`A ${next.name.toLowerCase()} holds ${next.capacity}: sold at any village shop`} />)}
          {filter !== 'all' && shown.length === 0 && <EmptyState glyph="☍" title="Nothing of that kind">Try another filter.</EmptyState>}
        </div>
        <footer className="fk-inv__pack-foot">
          {/* docs/136: a bigger pack is bought over a village shop's counter, never from here */}
          {next ? (
            <span className="fk-faint fk-inv__pack-note" data-testid="inv-pack-note">
              <OryxIcon name="item_147" scale={1} /> A {next.name.toLowerCase()} ({next.capacity} slots, {next.price}g) is sold at any village shop.
            </span>
          ) : <span className="fk-faint">The biggest pack there is.</span>}
          <div className={`fk-inv__trash ${overTrash ? 'is-over' : ''} ${dragItem !== undefined ? 'is-armed' : ''}`} data-drop="trash" data-testid="inv-trash" title="Drop an item here to throw it away">
            <span aria-hidden="true">🗑</span> Discard
          </div>
        </footer>
      </section>
      {trading && <TradeComposer worldId={worldId} fromHero={h.id} initialGive={trading} onClose={() => setTrading(undefined)} />}
    </div>
  );
}

/* ---------- numbers ---------- */

function StatsPanel({ g, preview }: { g: HeroGear; preview?: { label: string; stats?: HeroGear['stats']; off?: string[] } }) {
  const s = g.stats;
  const diff = s && preview?.stats ? statDiff(s, preview.stats) : {};
  const resource = s?.resource.kind ? cap(String(s.resource.kind).toLowerCase()) : 'Resource';
  const any = Object.keys(diff).length > 0;
  return (
    <div className="fk-inv__stats" aria-label="Numbers" data-testid="inv-stats">
      <div className="fk-inv__stats-head"><span className="fk-eyebrow">Numbers</span>{s && <span className="fk-inv__might" title="Capability: how the storyteller reads this hero's strength">Might <b>{s.capability}</b></span>}</div>
      {!s && <p className="fk-faint">The numbers are still loading.</p>}
      {s && (
        <ul className="fk-inv__stat-list">
          {STAT_ROWS.filter((r) => r.key !== 'resource' || s.resource.max > 0).map((r) => {
            const d = diff[r.key as StatKey];
            return (
              <li key={r.key} className={`fk-inv__stat ${d ? (d > 0 ? 'is-up' : 'is-down') : ''}`} data-testid="inv-stat" data-stat={r.key} data-value={statValue(s, r.key)} data-diff={d ?? 0}>
                <OryxIcon name={r.icon} scale={1} />
                <span className="fk-inv__stat-label">{r.key === 'resource' ? resource : r.label}</span>
                <span className="fk-inv__stat-value">{statValue(s, r.key)}{r.key === 'weapon_damage' && s.ranged_weapon ? <small> ranged</small> : null}</span>
                <span className="fk-inv__stat-diff">{d ? `${d > 0 ? '+' : ''}${d}` : ''}</span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="fk-inv__preview-note" aria-live="polite" data-testid="inv-preview-note">
        {preview?.stats ? (any ? <>If {preview.label}{preview.off?.length ? <>, the {preview.off.join(' and ')} go{preview.off.length === 1 ? 'es' : ''} to the pack</> : null}.</> : <>No change {preview.label}.</>) : <span className="fk-faint">Point at an item to see what it would change.</span>}
      </div>
    </div>
  );
}

/* ---------- details ---------- */

/** docs/72 IT17: the boons a hero carries into its next fights, with the fights left. */
function HeroBoons({ worldId, heroId }: { worldId: bigint; heroId: bigint }) {
  const boons = useHeroBoons(worldId).filter((b) => b.heroId === heroId);
  if (!boons.length) return null;
  return (
    <div className="fk-boons" data-testid="hero-boons" aria-label="Boons">
      {boons.map((b) => {
        const w = BOONS[b.card];
        return <span key={b.card} className="fk-boon" title={w ? `${w.name}: ${w.text}` : undefined} data-testid="hero-boon" data-card={b.card}><b>{w?.name ?? `Boon ${b.card}`}</b>{b.fightsLeft} fight{b.fightsLeft === 1 ? '' : 's'}</span>;
      })}
    </div>
  );
}

/** docs/83 PO8: the planned change, in words, with the way to call it off. */
function FightPlanNote({ name, moves, items, onClear, busy }: { name: string; moves: GearMoveView[]; items: GearItem[]; onClear(): void; busy: boolean }) {
  const line = moves.map((m) => `${items.find((i) => i.id === m.itemId)?.name ?? 'a piece'} → ${m.slot === null ? 'pack' : slotLabel(m.slot).toLowerCase()}`);
  return (
    <div className={`fk-inv__fightplan ${moves.length ? 'is-planned' : ''}`} role="status" data-testid="inv-fight-plan" data-moves={moves.length}>
      <span className="fk-inv__fightplan-tag">{moves.length ? 'Planned' : 'No foe in sight'}</span>
      {moves.length
        ? <span className="fk-inv__fightplan-line"><b>Change Gear</b> is {name}'s bonus action this turn: {line.join(' · ')}. It happens when the turn plays.</span>
        : <span className="fk-inv__fightplan-line">{name} can change gear as this turn's <b>bonus action</b>: drag pieces as usual and they move when the turn plays.</span>}
      {moves.length > 0 && <Button small variant="ghost" disabled={busy} onClick={onClear} data-testid="inv-plan-clear">Cancel changes</Button>}
    </div>
  );
}

function Details({ g, it, locked, busy, deployed, planning, inPuzzle, outfit, kitOf, onWear, onWearIn, onUnwear, onSell, onDiscard, onTrade, onUse }: {
  g: HeroGear; it?: GearItem; locked: boolean; busy: boolean; deployed: boolean; planning?: boolean; inPuzzle: boolean;
  /** docs/146: the outfitting rules (no selling; the kit stays in the company), and whose kit this piece is. */
  outfit: Outfitting; kitOf?: string;
  onWear(it: GearItem): void; onWearIn(it: GearItem, slot: number): void; onUnwear(it: GearItem): void; onSell(it: GearItem): void; onDiscard(it: GearItem): void; onTrade(it: GearItem): void; onUse(it: GearItem): void;
}) {
  const h = g.hero!;
  if (!it) {
    return (
      <div className="fk-inv__details fk-inv__details--empty" data-testid="inv-details-empty">
        <p className="fk-inv__howto"><b>Click</b> an item to see what it does. <b>Drag</b> it onto a slot to wear it, back to the pack to take it off, or onto another hero to hand it over. <b>Double-click</b> to wear or take off.</p>
        <ul className="fk-inv__rules">
          <li>One helm, one cape, one body, one belt, one pair each of gloves, legs and boots, one neckpiece, two rings.</li>
          <li>Two hands: one two-handed weapon, or a weapon and a shield, focus or second dagger.</li>
          <li>The pack holds {g.capacity}; a saddler sells bigger ones.</li>
          {outfit.mine && <li data-testid="inv-outfitting-rule">While you outfit, market goods move freely between your own heroes; starting kit stays with its hero, and selling and trading with other players open {outfit.gathering ? 'when the World begins' : 'once you set out'}.</li>}
        </ul>
      </div>
    );
  }
  const d = it.card ? itemDetails(it.card) : undefined;
  const usable = classAllowed(it.def.classMask, h.class);
  const price = sellPriceOf(it);
  const lore = it.cat ? undefined : loreOf(it.defId);
  const camp = it.slot === null ? campUse(it.cat) : undefined;
  const hint = !!camp && 'hint' in camp;
  const multi = it.slot === null && it.def.slots.length > 1;
  const torso = it.slot === SLOT.Torso;
  const kit = outfit.kit.has(it.id);
  const until = outfit.gathering ? 'until the World begins' : 'until your company sets out';
  return (
    <article className={`fk-inv__details fk-inv__details--${it.rarity}`} aria-label={`${it.name} details`} data-testid="inv-details" data-item-name={it.name}>
      <header className="fk-inv__details-head">
        <span className={`fk-inv__details-icon fk-inv__tile--${it.rarity}`}><ItemArtIcon def={it.defId} name={it.name} scale={4} dye={it.dye} /><UpgradeAura upgrades={it.upgrades} /></span>
        <div>
          <h3 className="fk-inv__details-name" data-testid="inv-details-name">{upgradedName(it.name, it.upgrades)}</h3>
          <div className="fk-inv__details-sub">
            <span className={`fk-inv__rarity fk-inv__rarity--${it.rarity}`}>{gradeWord(it.defId, it.tier, it.relic)}</span>
            {d && <span>{d.kind}</span>}
          </div>
          {d && <div className={`fk-inv__details-classes ${usable ? '' : 'is-bad'}`}>{usable ? d.classes : `${d.classes} — not a ${CLASS_NAMES[h.class]}`}</div>}
        </div>
      </header>
      {lore && <p className="fk-inv__lore">“{lore}”</p>}
      {it.cat ? <ItemBody item={it.cat} relicName={it.relicName} relicLore={it.relicLore} owner={{ ...g.holding, equipped: it.slot !== null }} upgrades={it.upgrades} /> : d ? (
        <ul className="fk-inv__effects" data-testid="inv-effects">{d.effects.length ? d.effects.map((e) => <li key={e}>{e}</li>) : <li className="fk-faint">No bonuses of its own.</li>}</ul>
      ) : it.relic ? <p className="fk-faint">A relic of this World's story. Its worth is in what it means.</p> : <p className="fk-faint">The details are still loading.</p>}
      {!it.cat && <UpgradeList upgrades={it.upgrades} />}
      {dyedLine(it.defId, it.dye) && <p className="fk-inv__dye" data-testid="inv-dye">{dyedLine(it.defId, it.dye)}</p>}
      <dl className="fk-inv__facts">
        <dt>Where</dt><dd>{it.slot !== null ? `Worn: ${slotLabel(it.slot).toLowerCase()}` : 'In the pack'}</dd>
        <dt>Worth</dt><dd>{it.relic ? 'Not for sale' : price ? `A shop pays ${price} gold` : 'Priceless to a shop'}</dd>
        {it.acquiredMs > 0 && <><dt>Since</dt><dd>{new Date(it.acquiredMs).toLocaleDateString([], { month: 'short', day: 'numeric' })}</dd></>}
        {it.offered && <><dt>Trade</dt><dd>Named in an open trade offer</dd></>}
        {kit && <><dt>Kit</dt><dd data-testid="inv-kit">{kitOf ? `${kitOf}'s` : 'A'} starting kit: Edit remakes it, so it stays with {kitOf ?? 'them'} {until}</dd></>}
      </dl>
      {it.def.consumable && <p className="fk-inv__note">{it.cat ? <>Used in a fight: its tile on the hotbar spends it.</> : <>Used in a fight: <b>Use Item</b> on the hotbar spends it.</>}</p>}
      <div className="fk-inv__actions" role="group" aria-label="Item actions">
        {camp && it.cat && !hint && campUseOffered(it.cat, { deployed, inPuzzle }) && <Button small variant="primary" disabled={locked || kit} title={kit ? `Starting kit is used up only once it is yours to keep (${until})` : undefined} onClick={() => onUse(it)} data-testid="inv-use">{campVerb(it.cat)}</Button>}
        {hint && it.cat && <Button small variant="primary" disabled={busy || !campUseOffered(it.cat, { deployed, inPuzzle })} title={inPuzzle ? 'The party’s next hint here is free' : 'Use it in a puzzle room'} onClick={() => onUse(it)} data-testid="inv-use">{campVerb(it.cat)}</Button>}
        {it.slot === null && it.def.slots.length > 0 && !multi && <Button small variant="primary" disabled={locked || !usable} onClick={() => onWear(it)} data-testid="inv-equip">Equip</Button>}
        {multi && it.def.slots.map((s) => <Button key={s} small variant={s === it.def.slots[0] ? 'primary' : 'secondary'} disabled={locked || !usable} onClick={() => onWearIn(it, s)} data-testid="inv-equip-to" data-slot={s}>Wear: {slotLabel(s).toLowerCase()}{s === SLOT.Trinket ? ' I' : s === SLOT.Trinket2 ? ' II' : ''}</Button>)}
        {it.slot !== null && <Button small disabled={locked || torso} title={torso ? 'The body is never bare: equip other armour to swap' : undefined} onClick={() => onUnwear(it)} data-testid="inv-unequip">Unequip</Button>}
        {!deployed && <Button small variant="ghost" disabled={locked || torso || kit} onClick={() => onTrade(it)} title={kit ? `Starting kit stays with its hero ${until}` : torso ? 'Swap the armour first' : 'Offer it to another hero'} data-testid="inv-trade" icon={<span aria-hidden="true">⇄</span>}>Trade…</Button>}
        {!deployed && !outfit.noSelling && !it.relic && price > 0 && <Button small variant="ghost" disabled={locked || torso} onClick={() => onSell(it)} data-testid="inv-sell" icon={<OryxIcon name="item_coin_bag" scale={1} />}>Sell {price}g</Button>}
        {!deployed && <Button small variant="danger" disabled={locked || it.relic || torso} title={it.relic ? 'A relic is not thrown away' : torso ? 'Swap the armour first' : 'Throw it away for good'} onClick={() => onDiscard(it)} data-testid="inv-discard">Discard</Button>}
      </div>
      {outfit.noSelling && !deployed && !it.relic && price > 0 && <p className="fk-faint fk-inv__outfit-note" data-testid="inv-no-sell">Nothing is sold while you outfit: there is no shop here.</p>}
      {deployed && <p className="fk-faint">{planning ? `In a fight, a change is planned: it happens as ${h.name}'s bonus action when the turn plays.` : `Nothing moves while ${h.name} is in the field.`}</p>}
    </article>
  );
}

/** The party chip's summary: what is worn as a strip of slots, the pack's fill, and the way in. */
export function GearStrip({ worldId, heroId, onOpen }: { worldId: bigint; heroId: bigint; onOpen(): void }) {
  const g = useHeroGear(worldId, heroId);
  const incoming = useIncomingTrades(worldId).filter((t) => t.toHero === heroId);
  const style = { '--fill': `${Math.min(100, (100 * g.pack.length) / g.capacity)}%` } as CSSProperties;
  return (
    <div className="fk-inv__strip" data-testid="gear-strip">
      <ul className="fk-inv__strip-slots" aria-label="Worn">
        {SLOTS.map((s) => {
          const w = g.worn.get(s.slot);
          return <li key={s.slot} className={`fk-inv__strip-slot ${w ? `fk-inv__tile--${w.rarity}` : 'is-empty'}`} title={w ? `${s.label}: ${upgradedName(w.name, w.upgrades)}` : `${s.label}: empty`} data-testid="gear-strip-slot" data-slot={s.slot} data-item-name={w?.name}>{w ? <ItemArtIcon def={w.defId} name={w.name} scale={1} dye={w.dye} /> : <OryxIcon name={s.icon} scale={1} />}{w && <UpgradeAura upgrades={w.upgrades} small />}</li>;
        })}
      </ul>
      <div className="fk-inv__strip-pack" style={style} title={`${g.pack.length} of ${g.capacity} in the pack`}><span>{g.pack.length}/{g.capacity}</span></div>
      <Button small variant="primary" onClick={onOpen} data-testid="open-inventory" icon={<OryxIcon name="item_147" scale={1} />}>Inventory{incoming.length > 0 && <Badge tone="amber" filled>{incoming.length}</Badge>}</Button>
    </div>
  );
}


/** docs/146: a hero's gear in one row of the company panel (World lobby, arrival modal): the ten
 *  worn slots and the pack's fill, the whole row a button into the inventory. A count marks pieces
 *  that arrived and the player has not looked at yet (a market buy, a piece handed over). */
export function GearRow({ worldId, heroId, name }: { worldId: bigint; heroId: bigint; name: string }) {
  const g = useHeroGear(worldId, heroId);
  const { isNew } = useSeen(g.items);
  // what arrived (a market buy, a piece handed over), not the hero's own starting kit
  const kit = useOutfitting(worldId).kit;
  const fresh = g.items.filter((i) => isNew(i) && kit.get(i.id) !== heroId).length;
  const style = { '--fill': `${Math.min(100, (100 * g.pack.length) / g.capacity)}%` } as CSSProperties;
  return (
    <button type="button" className="fk-inv__strip fk-inv__gear-row" onClick={() => useInventory.getState().open(heroId)} data-testid="lobby-hero-gear" data-hero-name={name}
      title={`${name}'s inventory: wear, swap and hand gear between your heroes`}>
      <span className="fk-inv__gear-row-label"><OryxIcon name="item_147" scale={1} />Gear{fresh > 0 && <span className="fk-inv__gear-row-new" data-testid="lobby-hero-gear-new" aria-label={`${fresh} new`}>{fresh}</span>}</span>
      <span className="fk-inv__strip-slots" aria-label="Worn">
        {SLOTS.map((sl) => {
          const w = g.worn.get(sl.slot);
          return <span key={sl.slot} className={`fk-inv__strip-slot ${w ? `fk-inv__tile--${w.rarity}` : 'is-empty'}`} title={w ? `${sl.label}: ${upgradedName(w.name, w.upgrades)}` : `${sl.label}: empty`} data-slot={sl.slot} data-item-name={w?.name}>{w ? <ItemArtIcon def={w.defId} name={w.name} scale={1} dye={w.dye} /> : <OryxIcon name={sl.icon} scale={1} />}</span>;
        })}
      </span>
      <span className="fk-inv__strip-pack" style={style} title={`${g.pack.length} of ${g.capacity} in the pack`}><span>{g.pack.length}/{g.capacity}</span></span>
    </button>
  );
}
